import React from 'react';
import { Panel } from 'primereact/panel';
import styled from 'styled-components';
import ToggleButton from 'components/v2/compiler/panels/toggleable/ToggleButton';
import { ExpandablePanelHeaderName, HeaderCaptionSemiBold } from 'styles/v2/Styles.styled';
import { ToolForOwnerModel } from 'types/tools/tool-owners/ToolForOwnerModel';
import { ToolForOwnerModelVersion } from 'types/tools/tool-owners/ToolForOwnerModelVersion';
import ToolFieldsGrid from './ToolFieldsGrid';
import ToolVersionStats from 'components/tools/ToolVersionStats';
import OldUIReadonlyTextInput from 'components/tools/OldUIReadonlyTextInput';
import OldUIReadonlySelectInput from 'components/tools/OldUIReadonlySelectInput';
import Tooltip from 'components/controls/Tooltip';
import { ToolVersionState } from 'types/tools/ToolVersionState';
import { HelpBox } from 'components/v2/help-boxes';

interface Props {
  toolData: ToolForOwnerModel;
  versionData: ToolForOwnerModelVersion;
}

const CompanyOwnedToolVersion: React.FC<Props> = ({ toolData, versionData }) => {
  const versionDisplayId = versionData.ord.toString().padStart(3, '0');

  return (
    <PanelWithoutAnnoyingStyles
      headerTemplate={(options) => (
        <div className={`${options.className} ${options.collapsed ? 'header-collapsed' : ''}`}>
          <ExpandablePanelHeaderName>
            <VersionNameLabel>{versionDisplayId} - tool version</VersionNameLabel>
            <ToggleButton options={options} />
          </ExpandablePanelHeaderName>

          <span>
            <i className={versionData.versionState === ToolVersionState.ENABLED ? 'pi pi-eye' : 'pi pi-eye-slash'} />
            <Tooltip position="title">
              {versionData.versionState === ToolVersionState.ENABLED
                ? 'The tool version is enabled'
                : 'The tool version is disabled'}
            </Tooltip>
          </span>
        </div>
      )}
      toggleable
    >
      <ToolFieldsGrid style={{ marginTop: 40 }}>
        <OldUIReadonlyTextInput
          label="Tool Version Number (Tool Owner)"
          tooltip="The tool version number as defined by the tool owner (e.g., 2.0.1). This is the version that will be visible to users and is separate from the internal version number assigned by EPD International."
          value={versionData.name}
        />

        <OldUIReadonlySelectInput label="Tool Verifier" value={versionData.verifierUser} />

        <OldUIReadonlyTextInput label="Tool Verifier Company" value={versionData.verifierCompanyName} />

        <OldUIReadonlyTextInput label="Tool Verifier Email" value={versionData.verifierUser?.email ?? ''} />

        <OldUIReadonlyTextInput label="Tool Approval Date by Verifier" value={versionData.verifierApprovalDate || ''} />

        <OldUIReadonlyTextInput
          label="Tool Approval Date by EPD International"
          value={versionData.adminApprovalDate || ''}
        />

        <OldUIReadonlyTextInput label="Validity Date" value={versionData.validityDate || ''} />

        <OldUIReadonlyTextInput
          label="PCR"
          tooltip="Product category rules"
          value={versionData.pcr?.fullName ?? ''}
          className="w-full"
        />

        <OldUIReadonlyTextInput
          label="C-PCR"
          tooltip="Complementary product category rules"
          value={versionData.cpcr?.fullName ?? ''}
          className="w-full"
        />

        <ToolVersionStats variant="toolOwner" toolData={toolData} versionData={versionData} />

        {versionData.latestStateChange && (
          <HelpBox className="w-full">
            <span>
              <HeaderCaptionSemiBold>
                Tool version {versionData.versionState === ToolVersionState.ENABLED ? 'enabled' : 'disabled'}
              </HeaderCaptionSemiBold>
              <br />
              <span>
                Tool Version was {versionData.versionState === ToolVersionState.ENABLED ? 'enabled' : 'disabled'} on{' '}
                {formatDateTimeVerbose(new Date(versionData.latestStateChange.date))} due to{' '}
                {versionData.latestStateChange.message}
              </span>
            </span>
          </HelpBox>
        )}
      </ToolFieldsGrid>
    </PanelWithoutAnnoyingStyles>
  );
};

export default CompanyOwnedToolVersion;

function formatDateTimeVerbose(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeStyle: 'long' }).format(date);
}

const PanelWithoutAnnoyingStyles = styled(Panel)`
  &&& .p-panel-header,
  &&& .p-panel-content {
    background: none;
    padding: 0;
    border: none;
  }
`;

const VersionNameLabel = styled.div`
  font-size: 16px;
  font-family: ${(props) => props.theme.fontFamilies.body};
  font-weight: 500;
`;
